import { useEffect, useRef } from 'react';
import useSyncOfflineData from 'src/utils/connectivity/useSyncOfflineData';
import { useToast } from 'src/app/useToast';
import { useSWService } from 'src/api/sw/useSWService';
import { useBunkerContext } from 'src/context/BunkerContext';
import { useAolStore } from 'src/context/AolContext';
import { useAolUpdateHandler } from 'src/app/useAolUpdateHandler';
import { useEventStore } from 'src/context/EventContext';
import { useVesselStore } from 'src/context/VesselContext';

const useBackgroundSyncRecovery = () => {
    const { syncOfflineData } = useSyncOfflineData();
    const { toastError } = useToast();
    const { refreshRequestsContaining } = useSWService();
    const { setIsSaving } = useBunkerContext();
    const { deletedAolSnapshots, setDeletedAolSnapshots, noonReports } = useAolStore();
    const { addNoonReport } = useAolUpdateHandler();
    const { setForceEventReloadDate } = useEventStore();
    const { selectedVessel } = useVesselStore();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handledDeletedGuidsRef = useRef<Set<string>>(new Set());
    const refreshRequestsContainingRef = useRef(refreshRequestsContaining);

    const deletedAolSnapshotsRef = useRef(deletedAolSnapshots);
    deletedAolSnapshotsRef.current = deletedAolSnapshots;
    const noonReportsRef = useRef(noonReports);
    noonReportsRef.current = noonReports;
    const addNoonReportRef = useRef(addNoonReport);
    addNoonReportRef.current = addNoonReport;
    const setDeletedAolSnapshotsRef = useRef(setDeletedAolSnapshots);
    setDeletedAolSnapshotsRef.current = setDeletedAolSnapshots;
    const setForceEventReloadDateRef = useRef(setForceEventReloadDate);
    setForceEventReloadDateRef.current = setForceEventReloadDate;
    const selectedVesselRef = useRef(selectedVessel);
    selectedVesselRef.current = selectedVessel;

    useEffect(() => {
        refreshRequestsContainingRef.current = refreshRequestsContaining;
    });

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'BACKGROUND_SAVE_COMPLETED') {
                const endpoint: string = event.data.endpoint ?? '';
                const url: string = event.data.url ?? '';
                if (endpoint === 'UpsertBunkering' || url.includes('UpsertBunkering')) {
                    void refreshRequestsContainingRef.current(['getBunkerings']);
                    // Delay clearing isSaving so the merge guard in BunkerRegistrationContainer
                    // remains active while React processes the incoming refresh data.
                    setTimeout(() => setIsSaving(false), 500);
                } else if (endpoint === 'DeleteNoonReport' || url.includes('DeleteNoonReport')) {
                    try {
                        const noonReportGuid = new URL(url).searchParams.get('noonReportGuid');
                        if (noonReportGuid) {
                            setDeletedAolSnapshotsRef.current((prev) => {
                                if (!prev.has(noonReportGuid)) return prev;
                                const next = new Map(prev);
                                next.delete(noonReportGuid);
                                return next;
                            });
                        }
                    } catch (_) {
                        // Ignore URL parsing errors
                    }
                }
                return;
            }

            if (event.data?.type !== 'BACKGROUND_SAVE_FAILED') return;

            const errorType = event.data.errorType;
            const url: string = event.data.url ?? '';
            console.warn('Background save failed:', event.data);

            // Clear bunkering save state immediately on any failure for UpsertBunkering
            if (url.includes('UpsertBunkering')) {
                setIsSaving(false);
            }

            if (errorType === 'http') {
                const status = event.data.status;

        // Failed DeleteNoonReport rollback
                if (url.includes('DeleteNoonReport')) {
                    const noonReportGuid = (() => {
                        try {
                            return new URL(url).searchParams.get('noonReportGuid');
                        } catch (_) {
                            return null;
                        }
                    })();

                    // Idempotent delete responses: 404/410 mean the resource is already absent on the server
                    if (status === 404 || status === 410) {
                        if (noonReportGuid) {
                            setDeletedAolSnapshotsRef.current((prev) => {
                                if (!prev.has(noonReportGuid)) return prev;
                                const next = new Map(prev);
                                next.delete(noonReportGuid);
                                return next;
                            });
                        }
                        void refreshRequestsContainingRef.current(['getNoonReportsForShip']);
                        return;
                    }

                    const executeRollback = async () => {
                        let restored = false;
                        if (noonReportGuid) {
                            const snapshot = deletedAolSnapshotsRef.current.get(noonReportGuid);
                            if (snapshot) {
                                // Only restore directly if the active vessel matches the snapshot's vessel
                                const isCurrentVessel =
                                    selectedVesselRef.current?.imoNumber &&
                                    snapshot.imoNumber === selectedVesselRef.current.imoNumber;

                                if (isCurrentVessel) {
                                    try {
                                        const result = await addNoonReportRef.current(snapshot.noonReport);
                                        const alreadyPresent = noonReportsRef.current?.some(
                                            (n) =>
                                                (snapshot.noonReport.metadata.noonReportGuid &&
                                                    n.metadata.noonReportGuid === snapshot.noonReport.metadata.noonReportGuid) ||
                                                n.metadata.noonReportId === snapshot.noonReport.metadata.noonReportId
                                        );

                                        if (result || alreadyPresent) {
                                            setDeletedAolSnapshotsRef.current((prev) => {
                                                const next = new Map(prev);
                                                next.delete(noonReportGuid);
                                                return next;
                                            });
                                            restored = true;
                                        }
                                    } catch (_) {
                                        restored = false;
                                    }
                                } else {
                                    // Vessel navigated away: do not insert into inactive vessel state;
                                    // invalidate CacheStorage so authoritative data is fetched on return.
                                    restored = false;
                                }
                            }
                        }

                        if (restored) {
                            toastError('Failed to delete report on server. Report has been restored.');
                        } else {
                            if (noonReportGuid) {
                                setDeletedAolSnapshotsRef.current((prev) => {
                                    const next = new Map(prev);
                                    next.delete(noonReportGuid);
                                    return next;
                                });
                            }
                            void refreshRequestsContainingRef.current(['getNoonReportsForShip']);
                            toastError('Failed to delete report on server. Please refresh to verify latest data.');
                        }
                    };

                    void executeRollback();
                    return;
                }

                // Failed DeleteEvent rollback (full reload)
                if (url.includes('DeleteEvent') || url.includes('DeleteEventsFor')) {
                    setForceEventReloadDateRef.current(new Date());
                    if (status === 409) {
                        toastError('This event cannot be deleted because a closing event exists. Delete the closing event first.', 8000);
                    } else {
                        toastError('Failed to delete event on server. Reloading events...');
                    }
                    return;
                }

                // 404 on save endpoints — report could not be found on the server.
                // Do NOT delete from local state; actual deletions are handled via SignalR.
                if (status === 404 && (url.includes('SaveNoonReportSection') || url.includes('UpdateSectionStatus'))) {
                    try {
                        const noonReportGuid = new URL(url).searchParams.get('noonReportGuid');
                        if (noonReportGuid && !handledDeletedGuidsRef.current.has(noonReportGuid)) {
                            handledDeletedGuidsRef.current.add(noonReportGuid);
                            setTimeout(() => handledDeletedGuidsRef.current.delete(noonReportGuid), 10000);
                            void refreshRequestsContainingRef.current(['getNoonReportsForShip']);
                            toastError('Save failed — the report could not be found on the server. Please refresh and try again.');
                        }
                    } catch (_) {
                        // URL parsing failed — fall through to generic error
                        toastError('Save failed — server returned an error. Please try again.');
                    }
                    return;
                }

                // Server rejected the request — notify user, don't retry
                toastError('Save failed — server returned an error. Please try again.');
                return;
            }

            // Network error (or legacy message without errorType) — the request has already been
            // persisted for offline retry by the service worker (see saveOfflinePostRequest in
            // fetch-handler.js), so no data is lost. Inform the user once per debounce window (not
            // once per failed message, to avoid a toast storm if several saves fail together while
            // offline) that their change is safe and will sync automatically once back online.
            const alreadyScheduled = debounceTimerRef.current !== null;
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (!alreadyScheduled) {
                toastError('No connection — your change has been saved locally and will sync automatically once you are back online.');
            }
            debounceTimerRef.current = setTimeout(() => {
                syncOfflineData();
                debounceTimerRef.current = null;
            }, 2000);
        };

        navigator.serviceWorker.addEventListener('message', handleMessage);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleMessage);
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [syncOfflineData, toastError, setIsSaving]);
};

export default useBackgroundSyncRecovery;
